import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faArrowLeft, faSearch, faFolder, faFolderPlus, faUserPlus, faCommentDots, faChartBar, faCheckCircle, faEllipsisV, faUsers, faTrashAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import PageHeader from "./layout/PageHeader";
import {
  countWellSkyClientStatuses,
  getClientStatusFilterKey,
  getClientWellSkyStatusLabel,
  getWellSkyDeactivationTooltip,
  isMonitoredCareHubClient,
  isWellSkyClient,
} from "../utils/clientWellSkyStatus";
import CreateFolderModal from './CreateFolderModal';
import AddClientModal from './AddClientModal';
import Modal from './Modal';
import CaregiverActivityBadge, { CaregiverActivityFlyout } from './CaregiverActivityBadge';
import { useCaregiverActivity } from '../hooks/useCaregiverActivity';
import './FolderView.css';
import ClientAvatar from "./ClientAvatar";
import { normalizeClientFolderId } from "../utils/clientDisplay";
import { resolveCompanyId } from "../utils/unityLocationScope";
import {
  createClientFolder,
  fetchClients,
  fetchClientsForScope,
  fetchClientFoldersForScope,
  createClient,
  fetchFolder,
  deleteClient,
  updateClientFolder,
  deleteClientFolder,
  updateClient,
} from '../utils/api';

const folderColors = [
  "#2196F3",
  "#4CAF50",
  "#FF9800",
  "#f44336",
  "#9C27B0",
  "#00BCD4",
  "#E91E63",
  "#607D8B",
];

// Caregiver Activity badge pilot: Unity-Princeton only, until confirmed for
// wider rollout. Both Unity-Princeton locations (the aggregate "Unity"
// location and the "Unity - Princeton" sub-location) are included.
const CAREGIVER_ACTIVITY_PILOT_LOCATION_IDS = new Set([
  "6943634bb4b9b2fe39890d5e",
  "6943634bb4b9b2fe39890d62",
]);

const FolderView = ({
  onCreateFolder,
  clientFolders: initialFolders = [],
  clients: initialClients = [],
  onCreateClient,
  activeLocation,
  locations = [],
  forceLocationView = false,
}) => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { folderId, locationId } = useParams();
  const routeLocationId = locationId || null;
  const routeId = locationId || folderId;
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState("active");
  const [careLevelFilter, setCareLevelFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const successToastTimerRef = useRef(null);
  
  // Local state for folders/clients
  const [folders, setFolders] = useState(initialFolders);
  const [folderClients, setFolderClients] = useState(initialClients.filter(c => c.folderId === folderId));
  const [loading, setLoading] = useState(false);
  const [fetchedCurrentFolder, setFetchedCurrentFolder] = useState(null);

  // Same locationId resolution used by loadData() below, reused for the
  // Caregiver Activity bulk fetch + pilot gating.
  const locId = routeLocationId || (activeLocation ? (activeLocation.id || activeLocation._id) : null);
  const isCaregiverActivityPilotEnabled = !!locId && CAREGIVER_ACTIVITY_PILOT_LOCATION_IDS.has(String(locId));
  const { data: caregiverActivity, isLoading: caregiverActivityLoading } = useCaregiverActivity({
    locationId: locId,
    enabled: isCaregiverActivityPilotEnabled,
  });
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isDeleteClientOpen, setIsDeleteClientOpen] = useState(false);
  const [isEditFolderModalOpen, setIsEditFolderModalOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [editFolderDescription, setEditFolderDescription] = useState("");
  const [editFolderColor, setEditFolderColor] = useState("#2196F3");
  const [isDeleteFolderOpen, setIsDeleteFolderOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [clientToEdit, setClientToEdit] = useState(null);
  const [clientMenu, setClientMenu] = useState(null); // { id, left, top }
  const [folderMenuOpenFor, setFolderMenuOpenFor] = useState(null);
  const [createParentFolderId, setCreateParentFolderId] = useState(null);
  const [caregiverActivityPopover, setCaregiverActivityPopover] = useState(null); // { id, left, top }

  useEffect(() => {
    const onDown = (e) => {
      // Close when clicking outside any kebab/menu.
      if (e?.target?.closest?.(".cm-row-menu")) return;
      if (e?.target?.closest?.(".cm-flyout-menu")) return;
      if (e?.target?.closest?.(".cm-folder-menu")) return;
      if (e?.target?.closest?.(".ca-badge-trigger")) return;
      if (e?.target?.closest?.(".ca-badge-flyout")) return;
      setClientMenu(null);
      setFolderMenuOpenFor(null);
      setCaregiverActivityPopover(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    return () => {
      if (successToastTimerRef.current) {
        clearTimeout(successToastTimerRef.current);
      }
    };
  }, []);

  const showSuccessToast = (message) => {
    setSuccessMessage(message);
    if (successToastTimerRef.current) {
      clearTimeout(successToastTimerRef.current);
    }
    successToastTimerRef.current = setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  // Auto-open New Client after creating a folder (triggered from ClientManagement or FolderView).
  useEffect(() => {
    if (routeLocation?.state?.autoOpenNewClient) {
      setIsAddClientModalOpen(true);
      navigate(routeLocation.pathname, { replace: true, state: {} });
    }
  }, [routeLocation, navigate]);

  // WellSky folders default to Active roster; reset when switching folder/location.
  useEffect(() => {
    setStatusFilter("active");
  }, [folderId, routeLocationId]);

  useEffect(() => {
    // If using API, we fetch here to ensure fresh data
    const loadData = async () => {
        setLoading(true);
        try {
            // Fetch folders for the current location if activeLocation is provided
            // Otherwise fetch all (or fallback logic)
            const locId = routeLocationId || (activeLocation ? (activeLocation.id || activeLocation._id) : null);
            const allFolders = await fetchClientFoldersForScope(
              activeLocation || { id: locId, _id: locId },
              locations,
            );
            setFolders(allFolders);

            // Fetch specific folder details if not found in the list (e.g. sub-folder not in root fetch?)
            // Actually fetchClientFolders usually returns all flat list. 
            // But let's try to find it first.
            const found = allFolders.find(f => (f.id === routeId || f._id === routeId));
            if (!found) {
                // If not found in list, try fetching specifically (if API supports it)
                try {
                    const specificFolder = await fetchFolder(routeId);
                    setFetchedCurrentFolder(specificFolder);
                } catch (err) {
                    console.warn("Could not fetch specific folder details", err);
                }
            }

            // Fetch clients for this folder
            // Optimize: create API endpoint like /api/clients?folderId=...
            // For now, fetch all and filter client side as per existing utility
            // We should also pass locationId to fetchClients if possible to strict filter
            const isLocationRoute = !!routeLocationId || (locId && (routeId === locId));
            const companyId = resolveCompanyId(activeLocation);
            let clientsForView;
            if (forceLocationView || isLocationRoute || !found) {
              clientsForView = await fetchClientsForScope(
                activeLocation || { id: locId, _id: locId },
                locations,
              );
            } else {
              clientsForView = await fetchClients({
                folderId: routeId,
                locationId: locId,
                companyId: companyId || undefined,
              });
              if (!clientsForView?.length) {
                const allClients = await fetchClientsForScope(
                  activeLocation || { id: locId, _id: locId },
                  locations,
                );
                const rid = String(routeId).trim();
                clientsForView = allClients.filter(
                  (c) => normalizeClientFolderId(c) === rid,
                );
              }
            }
            setFolderClients(clientsForView);
        } catch (error) {
            console.error("Error loading folder data:", error);
        } finally {
            setLoading(false);
        }
    };
    loadData();
  }, [folderId, locationId, activeLocation, locations, forceLocationView]);

  // Get current folder details for breadcrumb
  const currentFolder = folders.find(f => (f.id === routeId || f._id === routeId)) || fetchedCurrentFolder;
  const isLocationRoute = forceLocationView || !!routeLocationId || (activeLocation && (routeId === (activeLocation.id || activeLocation._id)));
  const folderName = currentFolder
    ? (currentFolder.name || currentFolder.year)
    : (isLocationRoute ? (activeLocation.name || activeLocation.label) : folderId);

  const getClientStatusLabel = (c) => getClientStatusFilterKey(c);

  const getWellSkyStatusPillClass = (client) => {
    const label = getClientWellSkyStatusLabel(client);
    if (label === "Active") return "cm-wellsky-status-active";
    if (label === "DeActive") return "cm-wellsky-status-deactive";
    return "cm-wellsky-status-unknown";
  };

  // NOTE: We keep folder hierarchy in the backend and show it ONLY in the left sidebar (tree).
  const [expandedFolderIds, setExpandedFolderIds] = useState(() => new Set());

  const foldersByParent = useMemo(() => {
    const map = new Map();
    (folders || []).forEach((f) => {
      const pid = f?.parentId || null;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(f);
    });
    for (const [, arr] of map.entries()) {
      arr.sort((a, b) =>
        String(a?.name || a?.year || "").localeCompare(String(b?.name || b?.year || "")),
      );
    }
    return map;
  }, [folders]);

  const getFolderChildren = (parentId) => foldersByParent.get(parentId || null) || [];
  const rootFolders = useMemo(() => getFolderChildren(null), [foldersByParent]);

  const toggleExpanded = (id) => {
    if (!id) return;
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getClientDisplayName = (c) => {
    const full =
      c?.name ||
      `${c?.firstName || ""} ${c?.lastName || ""}`.trim();
    return full || "Unnamed Client";
  };
  const formatDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  const calcAge = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    const diff = Date.now() - d.getTime();
    const ageDt = new Date(diff);
    const age = Math.abs(ageDt.getUTCFullYear() - 1970);
    return Number.isFinite(age) ? String(age) : "";
  };
  const getClientDob = (c) =>
    c?.dob ||
    c?.dateOfBirth ||
    c?.aipAnalysisData?.dob ||
    c?.aipAnalysisData?.dateOfBirth ||
    "";

  const careLevelFromScore = (score) => {
    const s = Number(score);
    if (!Number.isFinite(s)) return { text: "—", cls: "chip-muted" };
    // Higher AIP score = lower care level. Colors must match AIP tiers:
    // Low (green), Moderate (orange), High (red)
    if (s >= 75) return { text: "Low", cls: "chip-green" };
    if (s >= 40) return { text: "Moderate", cls: "chip-orange" };
    return { text: "High", cls: "chip-red" };
  };

  const getClientCareLevel = (c) => {
    // Always derive from AIP score to avoid conflicting tags.
    const s = Number(c?.aipScore);
    return careLevelFromScore(s).text;
  };
  const isClientAipProvisional = (c) =>
    c?.isAipProvisional === true ||
    String(c?.latestAssessmentStatus || "").toLowerCase() ===
      "auto-provisional";
  const careLevelChip = (score) => careLevelFromScore(score);
  // NOTE: We intentionally show only ONE Care Level chip (derived from AIP score)
  // so we don't display conflicting dual-tags like "High" + "At Risk".
  const getScoreTier = (score) => {
    const n = Number(score);
    if (!Number.isFinite(n)) return { label: "Needs Data", cls: "tier-nodata" };
    if (n >= 75) return { label: "Good", cls: "tier-good" };
    if (n >= 40) return { label: "Moderate", cls: "tier-moderate" };
    return { label: "At Risk", cls: "tier-risk" };
  };

  // Search / filter / sort (must come AFTER helper functions are defined)
  const filteredClients = useMemo(() => {
    const q = String(searchTerm || "").trim().toLowerCase();
    let list = (folderClients || []).filter((client) => {
      if (!q) return true;
      return (
        (client?.name || "").toLowerCase().includes(q) ||
        (client?.firstName || "").toLowerCase().includes(q) ||
        (client?.lastName || "").toLowerCase().includes(q)
      );
    });

    if (statusFilter === "active") {
      list = list.filter(isMonitoredCareHubClient);
    } else if (statusFilter !== "all") {
      list = list.filter((c) => getClientStatusLabel(c) === statusFilter);
    }

    if (careLevelFilter !== "all") {
      list = list.filter((c) => {
        const lvl = String(getClientCareLevel(c) || "").trim().toLowerCase();
        return lvl === careLevelFilter;
      });
    }

    const by = sortBy || "recent";
    const copy = [...list];
    if (by === "name") {
      copy.sort((a, b) =>
        getClientDisplayName(a).localeCompare(getClientDisplayName(b)),
      );
      return copy;
    }
    if (by === "score") {
      copy.sort((a, b) => {
        const sa = Number(a?.aipScore);
        const sb = Number(b?.aipScore);
        const na = Number.isFinite(sa) ? sa : -1;
        const nb = Number.isFinite(sb) ? sb : -1;
        return nb - na;
      });
      return copy;
    }
    // recent
    copy.sort((a, b) => {
      const da = new Date(
        a?.updatedAt || a?.updated_at || a?.lastUpdated || 0,
      ).getTime();
      const db = new Date(
        b?.updatedAt || b?.updated_at || b?.lastUpdated || 0,
      ).getTime();
      return (db || 0) - (da || 0);
    });
    return copy;
  }, [folderClients, searchTerm, statusFilter, careLevelFilter, sortBy]);

  const wellSkyStatusCounts = useMemo(
    () => countWellSkyClientStatuses(folderClients),
    [folderClients],
  );

  const handleCreateClient = async (newClientData) => {
    const locId = activeLocation ? activeLocation.id || activeLocation._id : null;
    if (!locId) {
      throw new Error("Please select a location before creating a client.");
    }
    if (!folderId) {
      throw new Error("Folder ID is missing. Please retry from a folder view.");
    }

    try {
      // Explicitly strip any clientId to let the backend generate it
      // and log payload for debugging
      const { clientId, ...clientDataWithoutId } = newClientData || {};
      const payload = {
        ...clientDataWithoutId,
        folderId,
        locationId: locId,
      };
      console.log("🚀 Creating client with payload (no clientId):", payload);
      const createdClient = await createClient(payload);

      // Update local state
      setFolderClients((prev) => [...prev, createdClient]);

      showSuccessToast("Client saved successfully");
      setIsAddClientModalOpen(false);

      // Notify parent if needed (optional if we manage state here)
      if (onCreateClient) onCreateClient(createdClient);
    } catch (error) {
      console.error("Failed to create client:", error);
      // Propagate error so modal can surface backend message
      throw error;
    }
  };

  const handleEditClient = (client) => {
    setClientToEdit(client);
    setIsEditClientModalOpen(true);
  };

  const handleUpdateClient = async (updates) => {
    try {
      if (!clientToEdit) return;
      const id = clientToEdit.id || clientToEdit._id;
      const payload = {
        ...updates,
      };
      const updated = await updateClient(id, payload);
      const resolvedFirst =
        updated?.firstName ?? updates?.firstName ?? clientToEdit?.firstName ?? "";
      const resolvedLast =
        updated?.lastName ?? updates?.lastName ?? clientToEdit?.lastName ?? "";
      const updatedWithName = {
        ...updated,
        name:
          updated?.name ||
          `${resolvedFirst} ${resolvedLast}`.trim() ||
          updated?.name,
        // Ensure address fields persist in local state (fallback if API omits them)
        street: updated?.street ?? updates?.street ?? clientToEdit?.street ?? "",
        city: updated?.city ?? updates?.city ?? clientToEdit?.city ?? "",
        state: updated?.state ?? updates?.state ?? clientToEdit?.state ?? "",
        zip: updated?.zip ?? updates?.zip ?? clientToEdit?.zip ?? "",
      };
      setFolderClients((prev) =>
        prev.map((c) => (c.id === id || c._id === id ? updatedWithName : c))
      );
      showSuccessToast('Client saved successfully');
    } catch (error) {
      console.error("Failed to update client:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update client.";
      alert(message);
    } finally {
      setIsEditClientModalOpen(false);
      setClientToEdit(null);
    }
  };

  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setIsDeleteClientOpen(true);
  };

  const confirmDeleteClient = async () => {
    try {
      if (!clientToDelete) return;
      const id = clientToDelete.id || clientToDelete._id;
      await deleteClient(id);
      setFolderClients((prev) => prev.filter((c) => (c.id || c._id) !== id));
      showSuccessToast('Client deleted successfully');
    } catch (error) {
      console.error('Failed to delete client:', error);
      alert('Failed to delete client.');
    } finally {
      setIsDeleteClientOpen(false);
      setClientToDelete(null);
    }
  };

  const cancelDeleteClient = () => {
    setIsDeleteClientOpen(false);
    setClientToDelete(null);
  };

  const handleEditFolder = (id) => {
    const target = folders.find((f) => f.id === id || f._id === id);
    if (!target) return;
    setFolderToEdit(target);
    setEditFolderName(target.name || target.year || "");
    setEditFolderDescription(target.description || "");
    setEditFolderColor(target.color || "#2196F3");
    setIsEditFolderModalOpen(true);
  };

  const handleUpdateFolder = async () => {
    try {
      if (!folderToEdit) return;
      const id = folderToEdit.id || folderToEdit._id;
      const payload = {
        name: editFolderName,
        description: editFolderDescription,
        color: editFolderColor,
      };
      const apiResult = await updateClientFolder(id, payload);
      const updatedFolder = apiResult?.folder || apiResult;
      setFolders((prev) =>
        prev.map((f) => (f.id === id || f._id === id ? updatedFolder : f))
      );
      showSuccessToast("Folder saved successfully");
      setIsEditFolderModalOpen(false);
      setFolderToEdit(null);
    } catch (error) {
      console.error("Failed to update folder:", error);
      alert("Failed to update folder.");
    }
  };

  const handleDeleteFolder = (id) => {
    const target = folders.find((f) => f.id === id || f._id === id);
    if (!target) return;
    setFolderToDelete(target);
    setIsDeleteFolderOpen(true);
  };

  const confirmDeleteFolder = async () => {
    try {
      if (!folderToDelete) return;
      const id = folderToDelete.id || folderToDelete._id;
      await deleteClientFolder(id);
      setFolders((prev) => prev.filter((f) => f.id !== id && f._id !== id));
      showSuccessToast("Folder deleted successfully");
      setIsDeleteFolderOpen(false);
      setFolderToDelete(null);
    } catch (error) {
      console.error("Failed to delete folder:", error);
      alert("Failed to delete folder.");
    }
  };

  const cancelDeleteFolder = () => {
    setIsDeleteFolderOpen(false);
    setFolderToDelete(null);
  };

  const handleCreateFolder = async (folderData) => {
      try {
        const locId =
          routeLocationId ||
          (activeLocation ? (activeLocation.id || activeLocation._id) : null);
        const payload = { 
            ...folderData, 
            // If user clicked "Create Sub Folder" on a folder row, use that as parent.
            // Otherwise:
            // - If we're inside a folder route, create as subfolder of current folder.
            // - If we're in location/root view, create a root folder.
            parentId:
              createParentFolderId != null
                ? createParentFolderId
                : !isLocationRoute
                  ? routeId
                  : null,
            locationId: locId
        };
        const createdFolder = await createClientFolder(payload);
        
        setFolders(prev => [...prev, createdFolder]);
        
        showSuccessToast('Folder saved successfully');
        setIsCreateFolderModalOpen(false);
        setCreateParentFolderId(null);
        
        if (onCreateFolder) onCreateFolder(createdFolder);

        if (payload.parentId) {
          setExpandedFolderIds((prev) => {
            const next = new Set(prev);
            next.add(payload.parentId);
            return next;
          });
        }

        const newId = createdFolder?.id || createdFolder?._id;
        if (newId) {
          navigate(`/client-management/${newId}`, {
            state: { autoOpenNewClient: true },
          });
        }
      } catch (error) {
          console.error("Failed to create folder:", error);
          alert("Failed to create folder.");
      }
  };

  return (
    <div className="folder-view-container">
       {successMessage && (
         <div className="cm-save-toast" role="status" aria-live="polite">
           <FontAwesomeIcon icon={faCheckCircle} /> {successMessage}
         </div>
       )}

      <PageHeader
        title="Client Management"
        subtitle="Manage client profiles, medical information, and care plans in one centralized location."
        icon={<FontAwesomeIcon icon={faUsers} className="text-2xl text-white" />}
        activeLocation={activeLocation}
        showControls={false}
      />

      <div className={`cm-shell${folderMenuOpenFor ? " cm-shell--menu-open" : ""}`}>
        <aside className="cm-sidebar">
          <div className="cm-sidebar-header">
            <div className="cm-sidebar-title">Folders</div>
          </div>

          <div className="cm-folder-list">
            {(() => {
              const renderNode = (f, level = 0) => {
                const id = f?.id || f?._id;
                const selected = !isLocationRoute && id && id === routeId;
                const displayName = f?.name || f?.year || "Unnamed Folder";
                const count = Number(f?.clientCount) || 0;
                const children = getFolderChildren(id);
                const hasChildren = children.length > 0;
                const isExpanded = expandedFolderIds.has(id);

                return (
                  <div key={id || `${displayName}-${level}`} className="cm-folder-node">
                    <div
                      role="button"
                      tabIndex={0}
                      className={`cm-folder-item ${selected ? "active" : ""}`}
                      style={{ paddingLeft: 10 + level * 16 }}
                      onClick={() => {
                        if (!id) return;
                        navigate(`/client-management/${id}`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (!id) return;
                          navigate(`/client-management/${id}`);
                        }
                      }}
                    >
                      <span
                        className="cm-folder-icon"
                        style={
                          f?.color && String(f.color).startsWith("#")
                            ? { backgroundColor: `${f.color}22`, color: f.color }
                            : {}
                        }
                      >
                        <FontAwesomeIcon icon={faFolder} />
                      </span>
                      <span className="cm-folder-text">
                        <span className="cm-folder-name" data-fullname={displayName}>
                          {displayName}
                        </span>
                      </span>
                      <span className="cm-folder-count">{count}</span>
                      <span className="cm-folder-menu">
                        <button
                          type="button"
                          className="cm-folder-kebab"
                          aria-label="Folder actions"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFolderMenuOpenFor((prev) => (prev === id ? null : id));
                          }}
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                        {folderMenuOpenFor === id && (
                          <div className="cm-menu cm-menu-folder">
                            <button
                              type="button"
                              className="cm-menu-item"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFolderMenuOpenFor(null);
                                setCreateParentFolderId(id);
                                setIsCreateFolderModalOpen(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faFolderPlus} style={{ marginRight: 10 }} />
                              Create Sub Folder
                            </button>
                            <button
                              type="button"
                              className="cm-menu-item"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFolderMenuOpenFor(null);
                                handleEditFolder(id);
                              }}
                            >
                              <FontAwesomeIcon icon={faEdit} style={{ marginRight: 10 }} />
                              Edit
                            </button>
                            <button
                              type="button"
                              className="cm-menu-item danger"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFolderMenuOpenFor(null);
                                handleDeleteFolder(id);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrashAlt} style={{ marginRight: 10 }} />
                              Delete
                            </button>
                          </div>
                        )}
                      </span>
                      {hasChildren && (
                        <button
                          type="button"
                          className="cm-folder-expand"
                          aria-label={isExpanded ? "Collapse subfolders" : "Expand subfolders"}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleExpanded(id);
                          }}
                        >
                          {isExpanded ? "−" : "+"}
                        </button>
                      )}
                    </div>
                    {hasChildren && isExpanded && (
                      <div className="cm-folder-children">
                        {children.map((c) => renderNode(c, level + 1))}
                      </div>
                    )}
                  </div>
                );
              };

              return rootFolders.map((f) => renderNode(f, 0));
            })()}
          </div>

          <button
            type="button"
            className="cm-new-folder"
            onClick={() => {
              setCreateParentFolderId(null);
              setIsCreateFolderModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faFolderPlus} /> New Folder
          </button>
        </aside>

        <main className="cm-main">
          <div className="cm-main-card">
            <div className="cm-main-header">
              <div className="cm-main-title-wrap">
                <div className="cm-main-title">{folderName}</div>
                <div className="cm-main-crumb">
                  <Link to="/client-management" className="cm-back-link">
                    <FontAwesomeIcon icon={faArrowLeft} /> Back to Client Management
                  </Link>
                </div>
              </div>
              <div className="cm-main-actions">
                <button
                  type="button"
                  className="cm-btn cm-btn-primary"
                  onClick={() => setIsAddClientModalOpen(true)}
                >
                  <FontAwesomeIcon icon={faUserPlus} /> New Client
                </button>
                <button
                  type="button"
                  className="cm-btn cm-btn-ask"
                  onClick={() => {
                    navigate("/ask-albert", {
                      state: {
                        locationId:
                          activeLocation?.id || activeLocation?._id || routeLocationId || null,
                        contextLabel: folderName || "this folder",
                        starterQuestion: `What is the most recent shift report note for clients in folder ${folderName || "this folder"}?`,
                        autoSend: true,
                      },
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faCommentDots} /> Ask Albert
                </button>
                <button type="button" className="cm-btn cm-btn-analytics">
                  <FontAwesomeIcon icon={faChartBar} /> Analytics
                </button>
              </div>
            </div>

            <div className="cm-filters">
              <div className="cm-search">
                <FontAwesomeIcon icon={faSearch} className="cm-search-icon" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {wellSkyStatusCounts.total > 0 ? (
                <div className="cm-status-summary" aria-label="WellSky client status totals">
                  <span className="cm-status-summary-pill cm-wellsky-status-active">
                    Active: {wellSkyStatusCounts.active}
                  </span>
                  <span className="cm-status-summary-pill cm-wellsky-status-deactive">
                    DeActive: {wellSkyStatusCounts.deactive}
                  </span>
                </div>
              ) : null}
              <select
                className="cm-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">
                  All Statuses
                  {wellSkyStatusCounts.total > 0
                    ? ` (${wellSkyStatusCounts.total})`
                    : ""}
                </option>
                <option value="active">
                  Active
                  {wellSkyStatusCounts.total > 0
                    ? ` (${wellSkyStatusCounts.active})`
                    : ""}
                </option>
                <option value="inactive">
                  DeActive
                  {wellSkyStatusCounts.total > 0
                    ? ` (${wellSkyStatusCounts.deactive})`
                    : ""}
                </option>
              </select>
              <select
                className="cm-select"
                value={careLevelFilter}
                onChange={(e) => setCareLevelFilter(e.target.value)}
              >
                <option value="all">All Care Levels</option>
                <option value="moderate">Moderate</option>
                <option value="high">High</option>
                <option value="low">Low</option>
              </select>
              <select
                className="cm-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recent">Sort: Recently Updated</option>
                <option value="name">Sort: Name</option>
                <option value="score">Sort: AIP Score</option>
              </select>
            </div>
          </div>

          <div className="cm-table-card">
            {loading ? (
              <div className="loading-state">Loading...</div>
            ) : filteredClients.length === 0 ? (
              <div className="empty-state">
                <p>This folder is empty.</p>
              </div>
            ) : (
              <div className="cm-table-clip">
                <table className="cm-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Care Level</th>
                      <th>Last Updated</th>
                      <th>AIP</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                  {filteredClients.map((client) => {
                    const id = client.id || client._id;
                    const name = getClientDisplayName(client);
                    const dob = getClientDob(client);
                    const age = calcAge(dob);
                    const updatedAt = client.updatedAt || client.updated_at || client.lastUpdated || "";
                    const score = Number.isFinite(Number(client.aipScore)) ? Math.round(Number(client.aipScore)) : null;
                    const tier = getScoreTier(score);
                    const careChip = careLevelChip(score);
                    const isAipProvisional = isClientAipProvisional(client);
                    const wellSkyStatusLabel = isWellSkyClient(client)
                      ? getClientWellSkyStatusLabel(client)
                      : null;

                    return (
                      <tr
                        key={id}
                        className="cm-row"
                        onClick={(e) => {
                          if (e?.target?.closest?.(".cm-row-menu")) return;
                          if (id) navigate(`/client-management/client/${id}/aip-analysis`);
                        }}
                      >
                        <td className="cm-name-cell">
                          <div className="cm-name">
                            <ClientAvatar
                              client={client}
                              className="cm-avatar"
                              initialClassName="cm-avatar-initial"
                              size={40}
                              alt={name}
                            />
                            <div className="cm-name-text">
                              <div className="cm-name-title">{name}</div>
                              <div className="cm-name-sub">
                                {formatDate(dob)}
                                {age ? ` (Age: ${age})` : ""}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {wellSkyStatusLabel ? (
                            <span
                              className={`cm-wellsky-status-pill ${getWellSkyStatusPillClass(client)}${
                                wellSkyStatusLabel === "DeActive"
                                  ? " cm-wellsky-status-has-tooltip"
                                  : ""
                              }`}
                              title={
                                wellSkyStatusLabel === "DeActive"
                                  ? getWellSkyDeactivationTooltip(client)
                                  : undefined
                              }
                            >
                              {wellSkyStatusLabel}
                            </span>
                          ) : (
                            <span className="cm-wellsky-status-pill cm-wellsky-status-unknown">
                              —
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`cm-pill ${careChip.cls}`}>{careChip.text}</span>
                        </td>
                        <td>{formatDate(updatedAt)}</td>
                        <td>
                          <div className="cm-aip-cell">
                            <span className={`cm-chip ${tier.cls}`}>
                              {score === null ? "Needs Data" : score}
                            </span>
                            {isAipProvisional && (
                              <span
                                className="cm-provisional-tag"
                                title="Based on WellSky care plan only"
                              >
                                Provisional
                              </span>
                            )}
                            {isCaregiverActivityPilotEnabled && (
                              <CaregiverActivityBadge
                                activity={caregiverActivity?.[id]}
                                loading={caregiverActivityLoading}
                                onToggle={(rect) => {
                                  setCaregiverActivityPopover((prev) =>
                                    prev?.id === id
                                      ? null
                                      : {
                                          id,
                                          left: Math.max(
                                            10,
                                            Math.min(
                                              rect.left,
                                              (window?.innerWidth || 0) - 240,
                                            ),
                                          ),
                                          top: rect.bottom + 8,
                                        },
                                  );
                                }}
                              />
                            )}
                          </div>
                        </td>
                        <td className="cm-menu-cell">
                          <div className="cm-row-menu">
                            <button
                              type="button"
                              className="cm-kebab"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const menuWidth = 180;
                                const menuHeight = 120;
                                const pad = 10;
                                let left = rect.right - menuWidth;
                                let top = rect.bottom + 8;
                                // Keep within viewport
                                left = Math.max(
                                  pad,
                                  Math.min(left, (window?.innerWidth || 0) - menuWidth - pad),
                                );
                                if (top + menuHeight > (window?.innerHeight || 0) - pad) {
                                  top = rect.top - menuHeight - 8;
                                }
                                top = Math.max(pad, top);
                                setClientMenu((prev) =>
                                  prev?.id === id ? null : { id, left, top },
                                );
                              }}
                            >
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {clientMenu?.id ? (
        <div
          className="cm-menu cm-flyout-menu"
          style={{ position: "fixed", left: clientMenu.left, top: clientMenu.top }}
          role="menu"
        >
          <button
            type="button"
            className="cm-menu-item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const client = filteredClients.find(
                (c) => (c.id || c._id) === clientMenu.id,
              );
              if (client) handleEditClient(client);
              setClientMenu(null);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="cm-menu-item danger"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const client = filteredClients.find(
                (c) => (c.id || c._id) === clientMenu.id,
              );
              if (client) handleDeleteClient(client);
              setClientMenu(null);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}

      {caregiverActivityPopover?.id ? (
        <CaregiverActivityFlyout
          activity={caregiverActivity?.[caregiverActivityPopover.id]}
          loading={caregiverActivityLoading}
          left={caregiverActivityPopover.left}
          top={caregiverActivityPopover.top}
        />
      ) : null}

       <CreateFolderModal 
         isOpen={isCreateFolderModalOpen} 
         onClose={() => {
           setIsCreateFolderModalOpen(false);
           setCreateParentFolderId(null);
         }}
         onCreate={handleCreateFolder} 
       />
       <AddClientModal 
         isOpen={isAddClientModalOpen} 
         onClose={() => setIsAddClientModalOpen(false)} 
         onCreate={handleCreateClient} 
       />
      <AddClientModal
        isOpen={isEditClientModalOpen}
        onClose={() => {
          setIsEditClientModalOpen(false);
          setClientToEdit(null);
        }}
        onSubmit={handleUpdateClient}
        initialClient={clientToEdit}
      />

      {isDeleteClientOpen && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <h3>Delete Client</h3>
            <p>
              Are you sure you want to delete client "
              <strong>{clientToDelete?.name || clientToDelete?.firstName || clientToDelete?.lastName}</strong>
              "?
            </p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={cancelDeleteClient}>
                Cancel
              </button>
              <button className="delete-confirm-button" onClick={confirmDeleteClient}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isEditFolderModalOpen}
        onClose={() => setIsEditFolderModalOpen(false)}
        title="Edit Folder"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdateFolder();
          }}
        >
          <div className="form-group">
            <label>Folder Name *</label>
            <input
              type="text"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              rows="3"
              value={editFolderDescription}
              onChange={(e) => setEditFolderDescription(e.target.value)}
            ></textarea>
          </div>
          <div className="form-group">
            <label>Folder Color</label>
            <div className="color-picker-grid">
              {folderColors.map((color) => (
                <div
                  key={color}
                  className={`color-swatch ${editFolderColor === color ? "selected" : ""}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setEditFolderColor(color)}
                />
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={() => setIsEditFolderModalOpen(false)}
            >
              CANCEL
            </button>
            <button type="submit" className="button update-button">
              UPDATE FOLDER
            </button>
          </div>
        </form>
      </Modal>

      {isDeleteFolderOpen && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <h3>Delete Folder</h3>
            <p>
              Are you sure you want to delete folder "
              <strong>{folderToDelete?.name || folderToDelete?.year}</strong>"?
            </p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={cancelDeleteFolder}>
                Cancel
              </button>
              <button className="delete-confirm-button" onClick={confirmDeleteFolder}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderView;
